import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import Modal from "../../components/common/modal";
import UploadImages from "../../components/upoloadimages";
import Button from "../../components/common/button";
import UploadDocument from "../../components/upoloadFiles";
import Toggle from "../../components/common/toggle";
import "react-country-state-city/dist/react-country-state-city.css";
import { CountrySelect } from "react-country-state-city";
import { getIndustryValidationSchema } from "../../validation/industryValidation";
import { resolveImageUrl } from "../../utils/resolveImageUrl";
import { appendIsActiveFields } from "../../utils/parseIsActive";

const createEmptyCatalog = () => ({
  id: `new-${Date.now()}-${Math.random()}`,
  title: "",
  description: "",
  existingFile: "",
  file: null,
});

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (["true", "1", "active", "enabled", "yes"].includes(s)) return true;
    if (["false", "0", "inactive", "disabled", "no"].includes(s)) return false;
  }
  return false;
};

const emptyValues = {
  title: "",
  description: "",
  location: "",
  slug: "",
  isActive: false,
  image: null,
};

const IndustryModal = ({
  isOpen,
  onClose,
  industry,
  mode,
  onSave,
  isLoading,
}) => {
  const [catalogs, setCatalogs] = useState([]);
  const [countrySelectKey, setCountrySelectKey] = useState(0);

  const isView = mode === "view";
  const isCreate = mode === "create";
  const isEditing = mode === "edit";
  const industryData = !isCreate ? industry?.data || industry : null;
  const prevIsOpenRef = useRef(false);
  const hasExistingImage = !!(
    industryData?.image?.url || industryData?.image
  );

  const formik = useFormik({
    enableReinitialize: isEditing && !!industryData,
    initialValues:
      isCreate || !industryData
        ? emptyValues
        : {
            title: industryData.title || "",
            description: industryData.description || "",
            location: industryData.location || industryData.country || "",
            slug: industryData.slug || "",
            isActive: toBool(
              typeof industryData.isActive !== "undefined"
                ? industryData.isActive
                : industryData.status,
            ),
            image: null,
          },
    validationSchema: getIndustryValidationSchema(
      isEditing,
      hasExistingImage,
    ),
    onSubmit: (values) => {
      const formDataObj = new FormData();

      formDataObj.append("title", values.title);
      formDataObj.append("description", values.description);
      formDataObj.append("location", values.location);
      formDataObj.append("slug", values.slug);
      if (!isCreate) {
        appendIsActiveFields(formDataObj, values.isActive);
      }

      if (values.image) {
        formDataObj.append("image", values.image);
      }

      catalogs.forEach((cat, index) => {
        if (cat._id) {
          formDataObj.append(`catlogs[${index}]._id`, cat._id);
        }

        formDataObj.append(`catlogs[${index}].title`, cat.title ?? "");
        formDataObj.append(
          `catlogs[${index}].description`,
          cat.description ?? "",
        );

        if (cat.existingFile && !cat.file) {
          formDataObj.append(
            `catlogs[${index}].existingFile`,
            cat.existingFile,
          );
        }

        if (cat.file) {
          formDataObj.append("catlogs", cat.file);
        }
      });

      onSave(formDataObj);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      prevIsOpenRef.current = false;
      return;
    }

    const justOpened = !prevIsOpenRef.current;
    prevIsOpenRef.current = true;

    setCountrySelectKey((k) => k + 1);

    if (industryData) {
      const rawCatlogs = industryData.catlogs || industryData.catelogs || [];
      setCatalogs(
        rawCatlogs.length
          ? rawCatlogs.map((cat, i) => ({
              id: cat._id || `existing-${i}`,
              _id: cat._id,
              title: cat.title || "",
              description: cat.description || "",
              file: null,
              existingFile: cat.file?.url || cat.file || "",
            }))
          : [createEmptyCatalog()],
      );
    } else {
      setCatalogs([createEmptyCatalog()]);
      if (justOpened && isCreate) {
        formik.resetForm({ values: emptyValues });
      }
    }
  }, [isOpen, industry, mode]);

  const handleCountryChange = (country) => {
    formik.setFieldValue("location", country.name);
    formik.setFieldTouched("location", true);
    setCountrySelectKey((k) => k + 1);
    requestAnimationFrame(() => {
      document.body.dispatchEvent(
        new MouseEvent("click", { bubbles: true }),
      );
    });
  };

  const getCatalogFileUrl = (filePath) => {
    if (!filePath || typeof filePath !== "string") return null;
    if (filePath.startsWith("http")) return filePath;
    return resolveImageUrl(filePath);
  };

  const addCatalog = () => {
    setCatalogs((prev) => [...prev, createEmptyCatalog()]);
  };

  const updateCatalog = (id, field, value) => {
    setCatalogs((prev) =>
      prev.map((cat) => (cat.id === id ? { ...cat, [field]: value } : cat)),
    );
  };

  const removeCatlog = (id) => {
    setCatalogs((prev) => prev.filter((cat) => cat.id !== id));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        mode === "delete"
          ? "Delete Industry"
          : industry
            ? "Edit Industry"
            : "Add Industry"
      }
    >
      {mode === "delete" ? (
        <>
          <div style={{ padding: "10px 2px" }}>
            <p style={{ margin: 0, color: "#333" }}>
              Are you sure you want to delete{" "}
              <strong>
                {industry?.title || industry?.data?.title || "this industry"}
              </strong>
              ?
            </p>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>
              This action will remove it from the database and the table.
            </p>
          </div>

          <div className="modal-footer">
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave?.()} disabled={isLoading}>
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="form-container">
            <div className="form-group">
              <label>Industry Image</label>
              {isView ? (
                (() => {
                  const imgSrc =
                    industryData?.image?.url || industryData?.image;

                  if (!imgSrc) return <div>No Image</div>;

                  const fullSrc = resolveImageUrl(imgSrc);

                  return (
                    <img
                      src={fullSrc}
                      alt="industry"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 10,
                      }}
                    />
                  );
                })()
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {isEditing && hasExistingImage && !formik.values.image && (
                    <img
                      src={resolveImageUrl(
                        industryData.image?.url || industryData.image
                      )}
                      alt="Current"
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #ddd",
                      }}
                    />
                  )}
                  <UploadImages
                    onChange={(file) => {
                      formik.setFieldValue("image", file);
                      formik.setFieldTouched("image", true);
                    }}
                    accept={"*"}
                  />
                  {formik.touched.image && formik.errors.image && (
                    <p className="error-text">{formik.errors.image}</p>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Title</label>
              <input
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isView}
              />
              {formik.touched.title && formik.errors.title && (
                <p className="error-text">{formik.errors.title}</p>
              )}
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label>Location</label>
              {isView ? (
                <input
                  name="location"
                  value={formik.values.location}
                  readOnly
                />
              ) : (
                <CountrySelect
                  key={`location-select-${countrySelectKey}`}
                  placeHolder="Select Country"
                  defaultValue={formik.values.location}
                  onChange={handleCountryChange}
                  onBlur={() => formik.setFieldTouched("location", true)}
                />
              )}
              {formik.touched.location && formik.errors.location && (
                <p className="error-text">{formik.errors.location}</p>
              )}
            </div>

            <div className="form-group">
              <label>Slug</label>
              <input
                name="slug"
                value={formik.values.slug}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isView}
              />
              {formik.touched.slug && formik.errors.slug && (
                <p className="error-text">{formik.errors.slug}</p>
              )}
            </div>

            {!isCreate && (
              <div
                className="form-group"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <label>Status</label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: formik.values.isActive ? "#10b981" : "#ef4444",
                    }}
                  >
                    {formik.values.isActive ? "Active" : "Inactive"}
                  </span>
                  <div className="sharing-toggle">
                    <Toggle
                      onChange={(newChecked) => {
                        formik.setFieldValue("isActive", newChecked);
                        formik.setFieldTouched("isActive", true);
                      }}
                      checked={!!formik.values.isActive}
                      disabled={isView}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-section" style={{ marginTop: 20 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h3 style={{ margin: 0, border: "none", padding: 0 }}>
                  Catalog Files{" "}
                  <span style={{ color: "#888", fontSize: 13, fontWeight: 500 }}>
                    ({catalogs.length})
                  </span>
                </h3>

                {!isView && (
                  <Button type="button" onClick={addCatalog}>
                    + Add Catalog
                  </Button>
                )}
              </div>

              {catalogs.map((cat, index) => {
                const fileUrl = getCatalogFileUrl(cat.existingFile);

                return (
                  <div
                    key={cat.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: 14,
                      borderRadius: 10,
                      marginBottom: 12,
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <strong style={{ fontSize: 13, color: "#555" }}>
                        Catalog #{index + 1}
                      </strong>

                      {!isView && catalogs.length > 1 && (
                        <Button
                          type="button"
                          onClick={() => removeCatlog(cat.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <div className="form-group" style={{ marginTop: 0 }}>
                      <label>Catalog Title</label>
                      <input
                        placeholder="Catalog Title"
                        value={cat.title}
                        onChange={(e) =>
                          updateCatalog(cat.id, "title", e.target.value)
                        }
                        disabled={isView}
                      />
                    </div>

                    <div className="form-group">
                      <label>Catalog Description</label>
                      <textarea
                        placeholder="Catalog Description"
                        value={cat.description}
                        onChange={(e) =>
                          updateCatalog(cat.id, "description", e.target.value)
                        }
                        disabled={isView}
                      />
                    </div>

                    <div className="form-group">
                      <label>Catalog File</label>
                      {isView ? (
                        fileUrl ? (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#5C308D", fontSize: 14 }}
                          >
                            {cat.existingFile.split("/").pop()}
                          </a>
                        ) : (
                          <span style={{ color: "#888", fontSize: 13 }}>
                            No file uploaded
                          </span>
                        )
                      ) : (
                        <UploadDocument
                          label="Upload Catalog File (PDF / CSV)"
                          onChange={(file) =>
                            updateCatalog(cat.id, "file", file)
                          }
                          existingFile={cat.existingFile}
                          selectedFile={cat.file}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            {!isView && (
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save"}
              </Button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default IndustryModal;
